import { motion, useMotionValue, useTransform } from 'framer-motion';
import { memo, useEffect } from 'react';
import { useAtomMotion } from '../../contexts/MotionContext';
import { useSelection } from '../../selection';
import { useFormatOverride } from '../../rendering/contexts/FormatOverrideContext';
import { computeBondEndPoints } from '@alchemiesolns/bond-calc-pure';
import type { BondRecipe } from '../../rendering/types/bondRecipe.types';
import { useFeedbackWithContext } from '../../feedback';

export interface DynamicBondHitboxProps {
  recipe: BondRecipe;
  onClick?: (e: React.MouseEvent<SVGElement>, bondId: string) => void;
  onMouseEnter?: (bondId: string) => void;
  onMouseLeave?: () => void;
}

export const DynamicBondHitbox = memo(function DynamicBondHitbox({
  recipe,
  onClick,
  onMouseEnter,
  onMouseLeave
}: DynamicBondHitboxProps) {
  const selection = useSelection({ optional: true });
  const format = useFormatOverride();
  const feedback = useFeedbackWithContext(recipe.bondId);

  // Get motion values for both atoms
  const atom1Motion = useAtomMotion(recipe.atom1Id);
  const atom2Motion = useAtomMotion(recipe.atom2Id);

  // If either atom has no motion values, don't render
  if (!atom1Motion || !atom2Motion) {
    return null;
  }

  // Check selection state (matches DynamicAtom behavior)
  const isSelected = selection?.isSelected(recipe.bondId) ?? false;
  const isHovered = selection?.isHovered(recipe.bondId) ?? false;

  // Sync selection state with FeedbackManager
  useEffect(() => {
    feedback.setSelected(isSelected);
  }, [isSelected, feedback]);

  useEffect(() => {
    feedback.setHovered(isHovered);
  }, [isHovered, feedback]);

  // Get opacity from FeedbackManager
  const opacity = 1;
  const fillOpacity = feedback.currentStyle.fillOpacity ?? 0;

  // Default interaction handlers
  const handleClick = (e: React.MouseEvent<SVGElement>) => {
    e.stopPropagation();
    if (onClick) {
      onClick(e, recipe.bondId);
    }
  };

  const handleMouseEnter = () => {
    if (onMouseEnter) {
      onMouseEnter(recipe.bondId);
    } else if (selection) {
      selection.setHovered(recipe.bondId);
    }
  };

  const handleMouseLeave = () => {
    if (onMouseLeave) {
      onMouseLeave();
    } else if (selection) {
      selection.setHovered(null);
    }
  };

  // Render hitbox for each tube/line in the bond
  return (
    <>
      {/* {recipe.tubes.map((tube) => ( */}
        <DynamicBondHitboxLine
          key={`${recipe.bondId}-hitbox-${recipe.tubes[0].lineIndex}`}
          recipe={recipe}
          tube={recipe.tubes[0]}
          atom1Motion={atom1Motion}
          atom2Motion={atom2Motion}
          format={format}
          opacity={opacity}
          fillOpacity={fillOpacity}
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          feedback={feedback}
        />
      {/* ))} */}
    </>
  );
});

/**
 * Individual hitbox line that tracks atom motion
 */
function DynamicBondHitboxLine({
  recipe,
  tube,
  atom1Motion,
  atom2Motion,
  format,
  opacity,
  fillOpacity,
  onClick,
  onMouseEnter,
  onMouseLeave,
  feedback
}: {
  recipe: BondRecipe;
  tube: any;
  atom1Motion: any;
  atom2Motion: any;
  format: any;
  opacity: number;
  fillOpacity: number;
  onClick: (e: React.MouseEvent<SVGElement>) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  feedback: any;
}) {
  // Motion values for computed endpoints
  const x1 = useMotionValue(0);
  const y1 = useMotionValue(0);
  const x2 = useMotionValue(0);
  const y2 = useMotionValue(0);

  // Update endpoint positions when atoms move
  useEffect(() => {
    const updateEndpoints = () => {
      const atom1X = atom1Motion.x.get();
      const atom1Y = atom1Motion.y.get();
      const atom2X = atom2Motion.x.get();
      const atom2Y = atom2Motion.y.get();

      const a1Pos = [atom1X, atom1Y, 0];
      const a2Pos = [atom2X, atom2Y, 0];

      // Calculate endpoints with perpendicular offset
      const endPoints = computeBondEndPoints(a1Pos, a2Pos, [0, 0, 1], 0);
        x1.set(endPoints.a1[0]);
        y1.set(endPoints.a1[1]);
        x2.set(endPoints.a2[0]);
        y2.set(endPoints.a2[1]);
    };

    // Initial update
    updateEndpoints();

    // Subscribe to atom position changes
    const unsubscribe1X = atom1Motion.x.on('change', updateEndpoints);
    const unsubscribe1Y = atom1Motion.y.on('change', updateEndpoints);
    const unsubscribe2X = atom2Motion.x.on('change', updateEndpoints);
    const unsubscribe2Y = atom2Motion.y.on('change', updateEndpoints);

    return () => {
      unsubscribe1X();
      unsubscribe1Y();
      unsubscribe2X();
      unsubscribe2Y();
    };
  }, [atom1Motion, atom2Motion, tube, recipe, format, x1, y1, x2, y2]);

  // Special bond rendering (wedge/dash) - use rect hitbox
  if (recipe.special && tube.lineIndex === 1) {
    // For special bonds, create a rectangular hitbox area
    // Calculate midpoint and dimensions
    const midX = useTransform([x1, x2], (latest) => ((latest as number[])[0] + (latest as number[])[1]) / 2);
    const midY = useTransform([y1, y2], (latest) => ((latest as number[])[0] + (latest as number[])[1]) / 2);

    const vectorX = useTransform([x1, x2], (latest) => (latest as number[])[1] - (latest as number[])[0]);
    const vectorY = useTransform([y1, y2], (latest) => (latest as number[])[1] - (latest as number[])[0]);
    const baseBondLength = useTransform(
      [vectorX, vectorY],
      (latest) => {
        const [vx, vy] = latest as number[];
        return Math.sqrt(vx * vx + vy * vy);
      }
    );

    // Extend bond length to include atom radius on both ends
    const atomRadius = format.atom.radius;
    const bondLength = useTransform(
      baseBondLength,
      (length) => (length as number) + (2 * atomRadius)
    );

    const hitboxWidth = recipe.special.boxX * 2;
    return (
      <>
      <motion.rect
        className="svg-bond-hitbox svg-bond-hitbox-special"
        data-bond-id={recipe.bondId}
        data-bond-index={String(recipe.bondIndex)}
        data-atom1-id={recipe.atom1Id}
        data-atom2-id={recipe.atom2Id}
        data-atom1-index={String(recipe.atom1Index)}
        data-atom2-index={String(recipe.atom2Index)}
        data-order={String(recipe.order)}
        data-line-index={String(tube.lineIndex)}
        data-visibility={recipe.visibility}
        data-special-type={recipe.special.type}
        x={useTransform(midX, (mx) => (mx as number) - hitboxWidth / 2)}
        y={useTransform([midY, bondLength], (latest) => {
          const [my, bl] = latest as number[];
          return my - bl / 2;
        })}
        width={hitboxWidth}
        height={bondLength}
        fill={feedback.currentStyle.bondColor ?? '#3479F6'}
        fillOpacity={fillOpacity}
        opacity={opacity}
        stroke={feedback.currentStyle.bondStroke}
        strokeWidth={feedback.currentStyle.bondStrokeWidth ?? 0}
        style={{ cursor: feedback.currentStyle.cursor ?? 'pointer' }}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      />
      </>
    );
  }

  // Regular bond hitbox - using rectangle for better hit detection
  // Calculate midpoint
  const midX = useTransform([x1, x2], (latest) => ((latest as number[])[0] + (latest as number[])[1]) / 2);
  const midY = useTransform([y1, y2], (latest) => ((latest as number[])[0] + (latest as number[])[1]) / 2);

  // Calculate bond vector and length
  const vectorX = useTransform([x1, x2], (latest) => (latest as number[])[1] - (latest as number[])[0]);
  const vectorY = useTransform([y1, y2], (latest) => (latest as number[])[1] - (latest as number[])[0]);
  const baseBondLength = useTransform(
    [vectorX, vectorY],
    (latest) => {
      const [vx, vy] = latest as number[];
      return Math.sqrt(vx * vx + vy * vy);
    }
  );

  // Extend bond length to include atom radius on both ends
  const atomRadius = format.atom.radius;
  const bondLength = useTransform(
    baseBondLength,
    (length) => (length as number) + (2 * atomRadius)
  );

  // Calculate rotation angle (in degrees)
  const rotationAngle = useTransform(
    [vectorX, vectorY],
    (latest) => {
      const [vx, vy] = latest as number[];
      return (Math.atan2(vy, vx) * 180) / Math.PI;
    }
  );

  // Hitbox width (thickness)
  const hitboxWidth = format.bond.thickness + (feedback.currentStyle.bondThickness ?? 2.6);

  return (
    <>
    <motion.rect
      className="svg-bond-hitbox"
      data-bond-id={recipe.bondId}
      data-bond-index={String(recipe.bondIndex)}
      data-atom1-id={recipe.atom1Id}
      data-atom2-id={recipe.atom2Id}
      data-atom1-index={String(recipe.atom1Index)}
      data-atom2-index={String(recipe.atom2Index)}
      data-order={String(recipe.order)}
      data-line-index={String(tube.lineIndex)}
      data-visibility={recipe.visibility}
      x={useTransform([midX, bondLength], (latest) => {
        const [mx, bl] = latest as number[];
        return mx - bl / 2;
      })}
      y={useTransform(midY, (my) => (my as number) - hitboxWidth / 2)}
      width={bondLength}
      height={hitboxWidth}
      rx={hitboxWidth / 2}
      ry={hitboxWidth / 2}
      fill={feedback.currentStyle.bondColor ?? '#3479F6'}
      fillOpacity={fillOpacity}
      opacity={opacity}
      stroke={feedback.currentStyle.hitboxStroke}
      strokeWidth={feedback.currentStyle.bondStrokeWidth ?? 0}
      style={{
        cursor: feedback.currentStyle.cursor ?? 'pointer',
        transformOrigin: 'center',
        rotate: rotationAngle,
      }}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    />
    </>
  );
}
